import ServiceWrapper, { type ServiceOptions } from "@utils/ServiceWrapper";
import {
    EPR_ACTION_WINDOWS,
    EPR_ARCHIVE_GRADING_PROFILES,
    EPR_DOWNLOAD,
    EPR_STATUS,
    EPR_TIMEPERIODS_BY_SCHOOL_YEAR
} from "@const/EprConst";

type NullableKey = number | string | null;

interface EprDaoOptions {
    action: string;
    gradingProfileId?: NullableKey;
    locationKey?: NullableKey;
    schoolYearKey?: NullableKey;
    studentNumber?: NullableKey;
    signal?: AbortSignal;
}

/**
 * Data Access Object for the EPR API.
 */
const EprDao = <T = unknown>(options: EprDaoOptions): Promise<{ data: T }> => {
    const { action, gradingProfileId, locationKey, schoolYearKey, studentNumber, signal } = options;

    const requestOptions: ServiceOptions = { url: "", method: "GET", signal };

    const actionConfig: Record<string, Partial<ServiceOptions>> = {
        archiveGradingProfilesRead: {
            method: "GET",
            url: `${EPR_ARCHIVE_GRADING_PROFILES}/${studentNumber}/${schoolYearKey}/index.json`
        },
        eprActionWindowsRead: {
            method: "GET",
            url: `${EPR_ACTION_WINDOWS}/${schoolYearKey}/LOCATION/${locationKey}/index.json`
        },
        eprPdfDownload: {
            headers: { Accept: "application/pdf" },
            method: "GET",
            responseType: "blob",
            url: `${EPR_DOWNLOAD}/${studentNumber}/${gradingProfileId}/detail.json`
        },
        eprStatusRead: { method: "GET", url: `${EPR_STATUS}/${studentNumber}/${schoolYearKey}/detail.json` },
        timePeriodsBySchoolYearRead: {
            method: "GET",
            url: `${EPR_TIMEPERIODS_BY_SCHOOL_YEAR}/${schoolYearKey}/index.json`
        }
    };

    const config = actionConfig[action];
    if (!config) {
        const error = new Error(`EprDao: Invalid action "${action}" provided`);
        console.error("EprDao: Invalid action provided");
        throw error;
    }

    return ServiceWrapper.serviceCall({
        options: { ...requestOptions, ...config }
    }) as Promise<{ data: T }>;
};

export default EprDao;
